import React, { useCallback } from 'react';

import {
  Button,
  DropdownMenu,
  H2,
  HStack,
  Icon,
  Link,
  Separator,
  ShimmerText,
  Tag,
  Text,
  VStack,
} from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';
import { type RecipeVariantProps } from '@betterment/samba-styled-system/types';
import { noop, partition } from 'es-toolkit/compat';

import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { CurrentEmployer, Employer, LoadingStatus } from './data';
import SidebarButton from './SidebarButton';

interface PlanSelectProps {
  currentEmployer?: CurrentEmployer | null;
  reload: () => void;
  loadingStatus: LoadingStatus;
  tasksLoadingStatus: LoadingStatus;
  employers?: Employer[];
  taskCounts: Record<string, number>;
  loadEmployersWithTasks: () => void;
}

type PlanSelectItemProps = {
  employer: Employer;
  isCurrent?: boolean;
  tasksLoadingStatus: LoadingStatus;
  taskCount?: number;
} & RecipeVariantProps<typeof planSelectItemStyles>;

const LoadingPlanSelectItem = () => {
  return (
    <HStack css={{ gap: 'space.800', pb: 'space.150' }}>
      <ShimmerText textStyle="text25" css={{ mx: 'space.300', my: 'space.100', width: '120px' }} />
      <ShimmerText textStyle="text25" css={{ mx: 'space.300', my: 'space.100', width: 'size.300' }} />
    </HStack>
  );
};

const InactivePlanSelectItem = ({ employer }: { employer: Employer }) => {
  return (
    <DropdownMenu.Item
      onSelect={() => {}}
      css={{
        borderRadius: 'medium',
        alignItems: 'flex-start',
        gap: 'space.150',
        bg: 'background.neutral.active',
      }}
    >
      <VStack>
        <Text>{employer.name}</Text>
        <Text variant="text25">Inactive</Text>
      </VStack>
    </DropdownMenu.Item>
  );
};

const PlanBadge = ({ tasksLoadingStatus, taskCount }: PlanSelectItemProps) => {
  const isLoading = tasksLoadingStatus === 'loading';

  if (isLoading) {
    return <ShimmerText textStyle="text25" css={{ width: 'size.300' }} />;
  }

  if (taskCount !== undefined && taskCount > 0) {
    return <Tag accent="blue">{taskCount}</Tag>;
  }

  return null;
};

const planSelectItemStyles = cva({
  base: {
    textAlign: 'left',
    borderRadius: 'medium',
    alignItems: 'flex-start',
  },
  variants: {
    isCurrent: {
      true: {
        bg: 'background.neutral.active',
      },
      false: {},
    },
  },
});

const PlanSelectItem = ({
  employer,
  tasksLoadingStatus,
  taskCount,
  isCurrent = false,
}: React.PropsWithChildren<PlanSelectItemProps>) => {
  return (
    <DropdownMenu.Item
      css={{ justifyContent: 'space-between' }}
      asChild={true}
      onSelect={noop}
      className={planSelectItemStyles({ isCurrent })}
    >
      <Link variant="ghostSecondary" href={urlBuilder.currentEmployer({ employerId: employer.id })}>
        <Text css={{ flexGrow: 1, maxWidth: '250px' }}>{employer.name}</Text>
        <PlanBadge employer={employer} tasksLoadingStatus={tasksLoadingStatus} taskCount={taskCount} />
      </Link>
    </DropdownMenu.Item>
  );
};

const isActive = (employer: Employer | null) => {
  return employer?.plan?.status === 'ACTIVE' || employer?.plan?.status === 'ONBOARDING';
};

const PlanSelect = ({
  currentEmployer,
  employers,
  taskCounts,
  loadingStatus,
  tasksLoadingStatus,
  reload,
  loadEmployersWithTasks,
}: PlanSelectProps) => {
  const [activeEmployers, inactiveEmployers] = partition(employers || [], isActive);

  // Handler to load employers with tasks when dropdown opens for the first time
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && tasksLoadingStatus === 'idle') {
        loadEmployersWithTasks();
      }
    },
    [tasksLoadingStatus, loadEmployersWithTasks]
  );

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild={true}>
        <SidebarButton label="Select plan">
          <Text variant="text25" color="text.onPrimary">
            Plan
          </Text>
          <Text title={currentEmployer?.name} color="text.onPrimary" variant="text50">
            {currentEmployer?.name}
          </Text>
        </SidebarButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        css={{
          zIndex: 5, // style-closet z-index for overlay elements
          px: 'space.100',
          py: 'space.150',
          minW: '300px',
          maxHeight: '100dvh',
          overflowY: 'auto',
        }}
        align="start"
        side="right"
      >
        <H2 variant="title200" css={{ px: 'space.300', my: 'space.200' }}>
          Select plan
        </H2>
        {loadingStatus === 'error' && (
          <HStack css={{ gap: 'space.100', p: 'space.200' }}>
            <Icon size="small" name="ExclamationCircle" />
            <VStack>
              <Text>Something went wrong</Text>
              <Button variant="textPrimary" onClick={reload}>
                Try again
              </Button>
            </VStack>
          </HStack>
        )}
        <VStack css={{ gap: 'space.100' }}>
          {(loadingStatus === 'loading' || loadingStatus === 'error') && (
            <>
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
            </>
          )}
          {currentEmployer && !isActive(currentEmployer) && (
            <>
              <InactivePlanSelectItem employer={currentEmployer} />
              <Separator />
            </>
          )}

          {loadingStatus === 'loaded' &&
            activeEmployers.map(employer => (
              <PlanSelectItem
                key={employer.id}
                employer={employer}
                tasksLoadingStatus={tasksLoadingStatus}
                taskCount={taskCounts[employer.id]}
                isCurrent={currentEmployer?.id === employer.id}
              />
            ))}

          {inactiveEmployers.length > 0 && (
            <>
              <Separator />
              <Link
                variant="textPrimary"
                css={{ d: 'block', p: 'space.200' }}
                href="/plans/inactive/replace-with-real-href"
              >
                Show inactive plans ({inactiveEmployers.length})
              </Link>
            </>
          )}
        </VStack>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default PlanSelect;
